import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { CalendarClock, Building2, Loader2 } from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import {
  facilities as api,
  facilityWaitlist,
  type Facility,
  type FacilityBooking,
  type FacilityAvailability,
  type ApiError,
} from '../../services/api';
import SearchSelect from '../../components/SearchSelect';

declare global {
  interface Window {
    Razorpay: new (options: Record<string, unknown>) => { open: () => void };
  }
}

let razorpayScriptPromise: Promise<boolean> | null = null;
function loadRazorpayScript(): Promise<boolean> {
  if (window.Razorpay) return Promise.resolve(true);
  if (razorpayScriptPromise) return razorpayScriptPromise;
  razorpayScriptPromise = new Promise(resolve => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
  return razorpayScriptPromise;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

const fmtAmount = (n: number | undefined) =>
  `₹${(n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const statusColor = (s: string) =>
  s === 'paid'   ? { bg: 'rgba(16,185,129,0.1)',  text: '#10B981' }
  : s === 'waived' ? { bg: 'rgba(16,185,129,0.1)', text: '#10B981' }
  : s === 'pending' ? { bg: 'rgba(245,158,11,0.1)', text: '#D97706' }
  : { bg: 'rgba(148,163,184,0.1)', text: '#64748B' };

const FacilityBookingPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;

  const [facilityList, setFacilityList] = useState<Facility[]>([]);
  const [facilityId, setFacilityId] = useState<string>('');
  const [date, setDate] = useState<string>(todayIso());

  const [availability, setAvailability] = useState<FacilityAvailability | null>(null);
  const [availLoading, setAvailLoading] = useState(false);
  const [availError, setAvailError] = useState('');

  const [bookingSlot, setBookingSlot] = useState<{ start: string; end: string } | null>(null);
  const [booking, setBooking] = useState(false);
  const [bookError, setBookError] = useState('');

  const [myBookings, setMyBookings] = useState<FacilityBooking[]>([]);
  const [myBookingsLoading, setMyBookingsLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const selectedFacility = useMemo(
    () => facilityList.find(f => f.id === facilityId) ?? null,
    [facilityList, facilityId],
  );

  useEffect(() => {
    if (!societyId) return;
    api.list(societyId).then(list => {
      setFacilityList(list);
      if (list.length > 0) setFacilityId(list[0].id);
    }).catch(() => {});
  }, [societyId]);

  const refreshAvailability = useCallback(() => {
    if (!societyId || !facilityId || !date) return;
    setAvailLoading(true); setAvailError('');
    api.getAvailability(societyId, facilityId, date)
      .then(setAvailability)
      .catch(err => setAvailError((err as ApiError).message || 'Failed to load availability'))
      .finally(() => setAvailLoading(false));
  }, [societyId, facilityId, date]);

  useEffect(() => { refreshAvailability(); }, [refreshAvailability]);

  const refreshMyBookings = useCallback(() => {
    if (!societyId || !facilityId || !user) return;
    setMyBookingsLoading(true);
    api.listBookings(societyId, facilityId)
      .then(list => setMyBookings(list.filter(b => b.residentId === user.id && b.status === 'confirmed')))
      .catch(() => {})
      .finally(() => setMyBookingsLoading(false));
  }, [societyId, facilityId, user]);

  useEffect(() => { refreshMyBookings(); }, [refreshMyBookings]);

  const startBooking = (slotStart: string, slotEnd: string) => {
    setBookingSlot({ start: slotStart, end: slotEnd });
    setBookError('');
  };

  const confirmBooking = async () => {
    if (!societyId || !facilityId || !user || !bookingSlot) return;
    setBooking(true); setBookError('');
    try {
      const created = await api.createBooking(societyId, facilityId, {
        residentId: user.id,
        bookedDate: date,
        slotStart: bookingSlot.start,
        slotEnd: bookingSlot.end,
      });

      const totalDue = (created.amount ?? 0) + (created.depositAmount ?? 0);
      if (totalDue > 0 && created.paymentStatus === 'pending') {
        const order = await api.createPaymentOrder(societyId, facilityId, created.id);
        const scriptLoaded = await loadRazorpayScript();
        if (!scriptLoaded) {
          setBookError('Booking created, but the payment gateway failed to load. You can retry payment from "My Bookings".');
          setBookingSlot(null);
          refreshAvailability(); refreshMyBookings();
          return;
        }
        const rzp = new window.Razorpay({
          key: order.keyId,
          amount: Math.round(order.amount * 100),
          currency: order.currency,
          name: 'Nexora',
          description: `${selectedFacility?.name ?? 'Facility'} booking — ${date}`,
          order_id: order.orderId,
          handler: async (response: { razorpay_payment_id: string; razorpay_signature: string }) => {
            try {
              await api.verifyPayment(societyId, facilityId, created.id, {
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
              });
            } catch (err) {
              setBookError((err as ApiError).message || 'Payment verification failed.');
            } finally {
              refreshAvailability(); refreshMyBookings();
            }
          },
          modal: { ondismiss: () => { refreshAvailability(); refreshMyBookings(); } },
        });
        setBookingSlot(null);
        rzp.open();
      } else {
        setBookingSlot(null);
        refreshAvailability(); refreshMyBookings();
      }
    } catch (err) {
      setBookError((err as ApiError).message || 'Failed to create booking');
    } finally {
      setBooking(false);
    }
  };

  const cancelBooking = async (bookingId: string) => {
    if (!societyId || !facilityId) return;
    setCancellingId(bookingId);
    try {
      await api.deleteBooking(societyId, facilityId, bookingId);
      refreshAvailability(); refreshMyBookings();
    } catch { /* ignore */ } finally { setCancellingId(null); }
  };

  const [waitlistMsg, setWaitlistMsg] = useState('');
  const joinWaitlist = async (slotStart: string, slotEnd: string) => {
    if (!societyId || !facilityId) return;
    try {
      await facilityWaitlist.join(societyId, facilityId, { requestedDate: date, slotStart, slotEnd });
      setWaitlistMsg(`You're on the waitlist for ${slotStart.slice(0, 5)}–${slotEnd.slice(0, 5)}. We'll notify you if it frees up.`);
    } catch (err) {
      setWaitlistMsg((err as ApiError).message || 'Failed to join waitlist.');
    }
  };

  const [guestPassMsg, setGuestPassMsg] = useState('');
  const issueGuestPass = async (bookingId: string) => {
    if (!societyId || !facilityId) return;
    const guestName = window.prompt("Guest's name?");
    if (!guestName) return;
    const guestPhone = window.prompt("Guest's phone (optional)?") || undefined;
    try {
      const pass = await facilityWaitlist.createGuestPass(societyId, facilityId, bookingId, { guestName, guestPhone });
      setGuestPassMsg(`Gate pass issued for ${guestName} — OTP: ${pass.otpCode}`);
    } catch (err) {
      setGuestPassMsg((err as ApiError).message || 'Failed to issue guest pass.');
    }
  };

  return (
    <PageLayout>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
          Book a Facility
        </h1>
        <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>Pick a facility and date to see available slots.</p>
      </div>

      <div className="nx-card" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 4 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 5, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              Facility
            </label>
            <SearchSelect
              value={facilityId}
              onChange={setFacilityId}
              options={facilityList.map(f => ({ value: f.id, label: f.name }))}
              placeholder="Search facilities…"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 5, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              Date
            </label>
            <input
              type="date" value={date} min={todayIso()} onChange={e => setDate(e.target.value)}
              style={{ width: '100%', padding: '9px 12px', background: 'var(--nx-surface-3)', border: '1px solid var(--nx-border)', borderRadius: 8, fontSize: 13, color: 'var(--nx-text-primary)' }}
            />
          </div>
        </div>
        {selectedFacility && (selectedFacility.bookingFee > 0 || selectedFacility.depositAmount > 0) && (
          <p style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 10 }}>
            Booking fee {fmtAmount(selectedFacility.bookingFee)} + deposit {fmtAmount(selectedFacility.depositAmount)} payable online.
          </p>
        )}
      </div>

      <div className="nx-card" style={{ padding: 20, marginBottom: 20 }}>
        <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 14 }}>
          Available Slots
        </h3>
        {availLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--nx-text-muted)', fontSize: 13 }}>
            <Loader2 size={14} className="nx-spin" /> Loading availability…
          </div>
        ) : availError ? (
          <p style={{ fontSize: 13, color: '#EF4444' }}>{availError}</p>
        ) : !selectedFacility ? (
          <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>Add a facility first to enable bookings.</p>
        ) : availability?.blackedOut ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#DC2626', fontSize: 13 }}>
            <Building2 size={16} /> Unavailable on this date{availability.blackoutReason ? ` — ${availability.blackoutReason}` : ''}.
          </div>
        ) : !availability || availability.slots.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>No bookable hours configured for this facility.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8 }}>
            {availability.slots.map(s => (
              s.available ? (
                <button
                  key={s.slotStart}
                  onClick={() => startBooking(s.slotStart, s.slotEnd)}
                  style={{
                    padding: '9px 10px', borderRadius: 8, fontSize: 12, fontWeight: 500,
                    border: '1px solid var(--nx-border)', cursor: 'pointer',
                    background: 'var(--nx-surface-1)', color: 'var(--nx-text-primary)',
                  }}
                >
                  {s.slotStart.slice(0, 5)}–{s.slotEnd.slice(0, 5)}
                </button>
              ) : (
                <button
                  key={s.slotStart}
                  onClick={() => joinWaitlist(s.slotStart, s.slotEnd)}
                  title="Join waitlist for this slot"
                  style={{
                    padding: '9px 10px', borderRadius: 8, fontSize: 11, fontWeight: 500,
                    border: '1px dashed var(--nx-border)', cursor: 'pointer',
                    background: 'var(--nx-surface-3)', color: 'var(--nx-text-muted)',
                  }}
                >
                  {s.slotStart.slice(0, 5)}–{s.slotEnd.slice(0, 5)}<br />Join waitlist
                </button>
              )
            ))}
          </div>
        )}
        {waitlistMsg && <p style={{ fontSize: 12, color: 'var(--nx-accent)', marginTop: 10 }}>{waitlistMsg}</p>}
      </div>

      {bookingSlot && (
        <div className="nx-card" style={{ padding: 20, marginBottom: 20, border: '1px solid var(--nx-border)' }}>
          <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 10 }}>
            Confirm Booking
          </h3>
          <p style={{ fontSize: 13, color: 'var(--nx-text-secondary)', marginBottom: 4 }}>
            {selectedFacility?.name} — {date}, {bookingSlot.start.slice(0, 5)}–{bookingSlot.end.slice(0, 5)}
          </p>
          {selectedFacility && (selectedFacility.bookingFee > 0 || selectedFacility.depositAmount > 0) && (
            <p style={{ fontSize: 13, color: 'var(--nx-text-secondary)', marginBottom: 10 }}>
              Total payable: {fmtAmount(selectedFacility.bookingFee + selectedFacility.depositAmount)}
            </p>
          )}
          {bookError && (
            <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, fontSize: 12, color: '#EF4444', marginBottom: 10 }}>
              {bookError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="nx-btn nx-btn-outline nx-btn-sm" onClick={() => setBookingSlot(null)} disabled={booking}>
              Cancel
            </button>
            <button className="nx-btn nx-btn-primary nx-btn-sm" onClick={confirmBooking} disabled={booking}>
              {booking ? 'Booking…' : 'Confirm & Pay'}
            </button>
          </div>
        </div>
      )}

      <div className="nx-card" style={{ padding: 20 }}>
        <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 14 }}>
          My Bookings — {selectedFacility?.name ?? ''}
        </h3>
        {myBookingsLoading ? (
          <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>Loading…</p>
        ) : myBookings.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--nx-text-muted)', fontSize: 13 }}>
            <CalendarClock size={16} /> No upcoming bookings for this facility.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {myBookings.map(b => {
              const sc = statusColor(b.paymentStatus);
              return (
                <div key={b.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 12px', background: 'var(--nx-surface-3)', borderRadius: 8, fontSize: 13,
                }}>
                  <span>{b.bookedDate}, {b.slotStart.slice(0, 5)}–{b.slotEnd.slice(0, 5)}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: sc.bg, color: sc.text }}>
                      {b.paymentStatus}
                    </span>
                    <button
                      className="nx-btn nx-btn-outline nx-btn-sm"
                      onClick={() => issueGuestPass(b.id)}
                    >
                      Guest Pass
                    </button>
                    <button
                      className="nx-btn nx-btn-outline nx-btn-sm"
                      onClick={() => cancelBooking(b.id)}
                      disabled={cancellingId === b.id}
                    >
                      {cancellingId === b.id ? 'Cancelling…' : 'Cancel'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {guestPassMsg && <p style={{ fontSize: 12, color: 'var(--nx-accent)', marginTop: 10 }}>{guestPassMsg}</p>}
      </div>
    </PageLayout>
  );
};

export default FacilityBookingPage;
